import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import axios from '../../config/axiosConfig';
import FeedbackForm from './FeedbackForm';
import Toast from '../Toast';
import { motion } from 'framer-motion';
import { formatCurrency, formatDate } from '../../utils/calculations';

/** Matches backend live-order flow; CLOSED (and history) stay out of Active orders. */
const CUSTOMER_ACTIVE_ORDER_STATUSES = ['PLACED', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED'];

const OrderStatusTracker = ({ contactNumber, restaurantId, tableId, refreshTrigger = 0 }) => {
    const [activeOrders, setActiveOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [showFeedbackForm, setShowFeedbackForm] = useState(null);
    const [orderFeedbacks, setOrderFeedbacks] = useState({});
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const eventSourceRef = useRef(null);
    const [expandedOrderIds, setExpandedOrderIds] = useState(() => new Set());

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const toggleOrderExpanded = (orderId) => {
        setExpandedOrderIds(prev => {
            const next = new Set(prev);
            if (next.has(orderId)) {
                next.delete(orderId);
            } else {
                next.add(orderId);
            }
            return next;
        });
    };

    const fetchActiveOrders = useCallback(async (isRefresh = false) => {
        try {
            if (isRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }
            const baseUrl = API_ENDPOINTS.CUSTOMER_ORDER_HISTORY(restaurantId, contactNumber);
            const url = isRefresh ? `${baseUrl}&_cb=${Date.now()}` : baseUrl;
            const response = await axios.get(url);
            const now = Date.now();
            const oneDayMs = 24 * 60 * 60 * 1000;
            const active = (response.data || []).filter((order) => {
                if (!order || !CUSTOMER_ACTIVE_ORDER_STATUSES.includes(order.status)) return false;
                const orderTimeRaw = order.orderDate || order.createdAt;
                if (!orderTimeRaw) return false;
                const orderTime = new Date(orderTimeRaw).getTime();
                if (Number.isNaN(orderTime)) return false;
                return now - orderTime < oneDayMs;
            });
            active.sort(
                (a, b) =>
                    new Date(b.orderDate || b.createdAt) - new Date(a.orderDate || a.createdAt)
            );
            setActiveOrders(active);
            if (isRefresh) {
                showToast('Orders refreshed successfully', 'success');
            }
        } catch (error) {
            console.error('Failed to fetch active orders:', error);
            if (isRefresh) {
                showToast('Failed to refresh orders', 'error');
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [contactNumber, restaurantId]);

    useEffect(() => {
        if (contactNumber && restaurantId) {
            fetchActiveOrders(false);
            setupSSE();
        }

        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, [contactNumber, restaurantId, fetchActiveOrders]);

    useEffect(() => {
        if (!refreshTrigger) return;
        if (!contactNumber || !restaurantId) return;
        fetchActiveOrders(true);
    }, [refreshTrigger, contactNumber, restaurantId, fetchActiveOrders]);


    const setupSSE = () => {
        try {
            eventSourceRef.current = new EventSource(`${API_BASE_URL}/orders/notifications/${restaurantId}?customerPhone=${contactNumber}`);
            
            eventSourceRef.current.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.type === 'NEW_ORDER' || data.type === 'ORDER_UPDATE') {
                    // Only refresh if the update is for this customer's order
                    if (data.data && data.data.mobileNumber === contactNumber) {
                        fetchActiveOrders(); // Refresh orders on update
                        if (data.updateType === 'item_status_update') {
                            showToast(`Item status updated for order #${data.data._id.slice(-6)}`, 'info');
                        }
                    }
                }
            };

            eventSourceRef.current.onerror = (error) => {
                console.error('SSE connection error:', error);
                // Reconnect after 5 seconds
                setTimeout(() => {
                    if (eventSourceRef.current) {
                        eventSourceRef.current.close();
                    }
                    setupSSE();
                }, 5000);
            };
        } catch (error) {
            console.error('Failed to setup SSE:', error);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PLACED':
                return 'bg-gray-100 text-gray-800';
            case 'ACCEPTED':
                return 'bg-blue-100 text-blue-800';
            case 'PREPARING':
                return 'bg-yellow-100 text-yellow-800';
            case 'READY':
                return 'bg-green-100 text-green-800';
            case 'SERVED':
                return 'bg-purple-100 text-purple-800';
            case 'CLOSED':
                return 'bg-gray-200 text-gray-700';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'PLACED':
                return '📝';
            case 'ACCEPTED':
                return '✅';
            case 'PREPARING':
                return '👨‍🍳';
            case 'READY':
                return '🔔';
            case 'SERVED':
                return '🍽️';
            case 'CLOSED':
                return '✔️';
            default:
                return '📋';
        }
    };

    const getStatusMessage = (status) => {
        switch (status) {
            case 'PLACED':
                return 'Your order has been placed and is waiting to be accepted.';
            case 'ACCEPTED':
                return 'Your order has been accepted and will be prepared soon.';
            case 'PREPARING':
                return 'Your order is being prepared in the kitchen.';
            case 'READY':
                return 'Your order is ready! The waiter will serve it shortly.';
            case 'SERVED':
                return 'Your order has been served. Enjoy your meal!';
            default:
                return 'Order status: ' + status;
        }
    };

    const getItemStatusColor = (status) => {
        switch (status) {
            case 'PENDING':
                return 'bg-gray-100 text-gray-700';
            case 'COOKING':
                return 'bg-orange-100 text-orange-700';
            case 'PREPARING':
                return 'bg-yellow-100 text-yellow-700';
            case 'READY':
                return 'bg-green-100 text-green-700';
            case 'SERVED':
                return 'bg-blue-100 text-blue-700';
            case 'DONE':
                return 'bg-purple-100 text-purple-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    const getItemStatusIcon = (status) => {
        switch (status) {
            case 'PENDING':
                return '⏳';
            case 'COOKING':
                return '👨‍🍳';
            case 'PREPARING':
                return '⏰';
            case 'READY':
                return '✅';
            case 'SERVED':
                return '🍽️';
            case 'DONE':
                return '✔️';
            default:
                return '⏳';
        }
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
        );
    }

    if (activeOrders.length === 0) {
        return null; // Don't show if no active orders
    }

    return (
        <div className="space-y-4 mb-6">
            <h3 className="text-xl font-bold text-gray-800 flex items-center space-x-2">
                <span>📊</span>
                <span>Order Status</span>
            </h3>
            <p className="text-xs text-stone-500 -mt-1">
                If you want to edit the order, please contact the waiter or restaurant owner by clicking on the bell icon popup below.
            </p>

            {activeOrders.map((order) => (
                <motion.div
                    key={order._id}
                    className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    {(() => {
                        const isExpanded = expandedOrderIds.has(order._id);
                        return (
                            <>
                                <div className="px-4 py-3 sm:px-5 sm:py-4 border-l-4 border-blue-500 bg-gradient-to-r from-slate-50 to-white">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-700 text-sm">
                                                    🧾
                                                </span>
                                                <div className="min-w-0">
                                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                                        Order
                                                    </p>
                                                    <p className="text-sm sm:text-base font-bold text-slate-900 truncate">
                                                        #{order._id.slice(-6)}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-[11px] text-slate-500">
                                                Placed: {formatDate(order.orderDate)}
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-end gap-2">
                                            <span className={`px-3 py-1 rounded-full text-[11px] font-semibold ${getStatusColor(order.status)}`}>
                                                {getStatusIcon(order.status)} {order.status}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => toggleOrderExpanded(order._id)}
                                                className="inline-flex items-center justify-center rounded-full border border-stone-200 bg-white px-3 py-1 text-[11px] font-semibold text-stone-600 hover:bg-stone-50"
                                                aria-label={isExpanded ? 'Hide order details' : 'Show order details'}
                                            >
                                                {isExpanded ? 'Hide' : 'Show'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="px-4 pb-4 pt-3 sm:px-5 sm:pb-5 sm:pt-4 space-y-3">
                                        {(() => {
                                            const baseTotal = Number(order.totalAmount || 0);
                                            const taxAmt = Number(order.taxAmount || 0);
                                            const platformFee = Number(order.serviceAmount || 0);
                                            const finalTotal = Number(order.finalAmount || (baseTotal + taxAmt + platformFee));
                                            return (
                                                <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5">
                                                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-500 mb-2">
                                                        Price summary
                                                    </p>
                                                    <div className="space-y-1.5 text-xs">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-stone-600">Base total</span>
                                                            <span className="font-medium text-stone-800">₹{baseTotal.toFixed(2)}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-stone-600">Tax</span>
                                                            <span className="font-medium text-stone-800">₹{taxAmt.toFixed(2)}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-stone-600">Platform fee</span>
                                                            <span className="font-medium text-stone-800">₹{platformFee.toFixed(2)}</span>
                                                        </div>
                                                        <div className="pt-1 mt-1 border-t border-stone-200 flex items-center justify-between">
                                                            <span className="font-semibold text-stone-800">Final total</span>
                                                            <span className="font-bold text-emerald-600">₹{finalTotal.toFixed(2)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        <p className="text-xs text-slate-600">
                                            {getStatusMessage(order.status)}
                                        </p>

                                        {/* Order Items Summary with Status */}
                                        <div className="mb-4">
                                            <p className="text-sm font-semibold mb-2">Items Status:</p>
                                            <div className="space-y-2">
                                                {order.items?.map((item, idx) => {
                                                    const itemStatus = item.status || 'PENDING';
                                                    return (
                                                        <div key={idx} className="flex justify-between items-center text-sm bg-slate-50 p-2 rounded-lg">
                                                            <div className="flex items-center gap-2 flex-1">
                                                                <span>{item.name} x {item.quantity}</span>
                                                                <span className={`px-2 py-1 rounded text-xs font-semibold ${getItemStatusColor(itemStatus)}`}>
                                                                    {getItemStatusIcon(itemStatus)} {itemStatus}
                                                                </span>
                                                            </div>
                                                            <span className="font-medium">₹{item.price * item.quantity}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className="flex justify-between items-center pt-3 border-t border-stone-100">
                                            <span className="text-xs text-stone-500">Total</span>
                                            <span className="font-bold text-emerald-600">
                                                ₹{order.finalAmount || order.totalAmount || 0}
                                            </span>
                                        </div>

                                        {/* Progress Indicator */}
                                        <div className="mt-4">
                                            <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                                                <span>Order Progress</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    className={`h-2 rounded-full transition-all duration-500 ${
                                                        order.status === 'PLACED' ? 'bg-gray-400 w-1/6' :
                                                        order.status === 'ACCEPTED' ? 'bg-blue-500 w-2/6' :
                                                        order.status === 'PREPARING' ? 'bg-yellow-500 w-3/6' :
                                                        order.status === 'READY' ? 'bg-green-500 w-5/6' :
                                                        order.status === 'SERVED' ? 'bg-purple-500 w-full' :
                                                        'bg-gray-400 w-1/6'
                                                    }`}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </motion.div>
            ))}

            {/* Feedback Form - Only show for closed orders */}
            {showFeedbackForm && (
                <FeedbackForm
                    orderId={showFeedbackForm}
                    order={activeOrders.find(o => o._id === showFeedbackForm)}
                    onClose={() => {
                        setShowFeedbackForm(null);
                    }}
                    onSubmitted={() => {
                        setShowFeedbackForm(null);
                    }}
                />
            )}
            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default OrderStatusTracker;

